/**
 * Сборка итога сессии.
 *
 * XP начисляется здесь, но только за полезные действия и с убывающей отдачей:
 * список причин закрыт, произвольного начисления не существует (ТЗ FR-GM-04).
 */

import type {
  Baseline,
  MasteryStat,
  MasteryUnit,
  TypingMetrics,
  XpReason,
  XpTransaction,
} from '@type2x/shared-contracts';
import { MASTERED_THRESHOLD, TARGET_ACCURACY, WEAK_THRESHOLD } from '@type2x/shared-contracts';
import {
  awardXp,
  buildGrowthReport,
  computeIndicators,
  levelForXp,
  type LearningConfig,
} from '@type2x/learning-engine';

import type { Recommendation, SessionOutcomeSummary, WeakZone } from '../contracts/summary.js';
import { MAX_RECOMMENDATIONS, MAX_WEAK_ZONES } from '../contracts/summary.js';

/** Данные, из которых собирается итог. */
export interface SummaryInput {
  readonly metrics: TypingMetrics;
  readonly baseline: Baseline | null;
  readonly units: Readonly<Record<string, MasteryUnit>>;
  readonly statsBefore: Readonly<Record<string, MasteryStat>>;
  readonly statsAfter: readonly MasteryStat[];
  readonly previousXp: number;
  /** Сколько раз каждая причина уже сработала сегодня. */
  readonly xpOccurrencesToday: Readonly<Partial<Record<XpReason, number>>>;
  readonly activeDays: number;
  readonly retained: boolean;
  readonly isControlled: boolean;
  readonly awardedAt: string;
  readonly config: LearningConfig;
}

function reasonFor(stat: MasteryStat, config: LearningConfig): WeakZone['reason'] {
  if (stat.errorRate > 0.05) return 'errors';
  if (stat.correctionRate > 0.05) return 'corrections';
  return stat.medianLatencyMs > config.hesitationThresholdMs ? 'hesitation' : 'errors';
}

/** Слабые зоны по итогам сессии, по убыванию проблемности. */
export function collectWeakZones(input: SummaryInput): WeakZone[] {
  return input.statsAfter
    .filter((stat) => stat.masteryScore < WEAK_THRESHOLD)
    .map((stat) => ({
      content: input.units[stat.unitId]?.content ?? stat.unitId,
      reason: reasonFor(stat, input.config),
      severity: 1 - stat.masteryScore,
    }))
    .sort((a, b) => b.severity - a.severity || a.content.localeCompare(b.content))
    .slice(0, MAX_WEAK_ZONES);
}

/** Причины начисления XP по итогам сессии. */
export function collectXpReasons(input: SummaryInput): XpReason[] {
  const reasons: XpReason[] = ['session-completed'];

  const improvedAccuracy = input.statsAfter.some((stat) => {
    const before = input.statsBefore[stat.unitId];
    return before !== undefined && stat.errorRate < before.errorRate;
  });
  if (improvedAccuracy) reasons.push('accuracy-improved');

  const newlyMastered = input.statsAfter.some((stat) => {
    const before = input.statsBefore[stat.unitId];
    return (
      stat.masteryScore >= MASTERED_THRESHOLD &&
      (before === undefined || before.masteryScore < MASTERED_THRESHOLD)
    );
  });
  if (newlyMastered) reasons.push('new-unit-mastered');

  const fixedPersistentError = input.statsAfter.some((stat) => {
    const before = input.statsBefore[stat.unitId];
    return before !== undefined && before.errorRate > 0.3 && stat.errorRate < 0.1;
  });
  if (fixedPersistentError) reasons.push('persistent-error-fixed');

  if (input.isControlled) reasons.push('control-test-completed');
  if (input.retained) reasons.push('skill-retained');

  return reasons;
}

/**
 * Ниже этой стабильности ритм считается рваным: похоже, человек устал.
 *
 * Порог не менялся с первого дня и по-прежнему выбран не по данным: ритм —
 * единственная метрика, которая не уходит на сервер, и распределения его
 * значений у нас нет. Менять число «на глаз» хуже, чем оставить как есть
 * (ловушка из CLAUDE.md).
 */
const RHYTHM_REST_THRESHOLD = 0.4;

/**
 * Не более трёх рекомендаций, упорядоченных по важности.
 *
 * Первой всегда идёт точность: сначала точность, затем скорость (ТЗ §7.7).
 *
 * Советы не спорят друг с другом. Владелец принёс с живого использования
 * экран, где рядом стояли «точность высокая — можно повышать темп» и «ритм
 * рваный, сделайте перерыв»: обе строки верны по отдельности, а вместе
 * читаются как «ускорьтесь и остановитесь». Рваный ритм — это про состояние
 * человека, а не про материал: пока он рваный, «прибавьте» и «идите на
 * контрольный тест» не советуются вовсе. Что тренировать потом — «повторите
 * слабые слова» — сказать при этом можно: перерыв говорит когда, а не что.
 */
export function buildRecommendations(
  input: SummaryInput,
  weakZones: readonly WeakZone[],
): Recommendation[] {
  const recommendations: Recommendation[] = [];
  const [minAccuracy] = input.config.targetAccuracyRange;

  const tired =
    input.metrics.rhythmStability < RHYTHM_REST_THRESHOLD && input.metrics.typedCharacters > 0;

  if (input.metrics.accuracy < minAccuracy) {
    recommendations.push({ kind: 'accuracy' });
  }
  // Перерыв стоит выше выбора материала: он отвечает на «продолжать ли
  // сейчас», а остальные — на «чем заняться».
  if (tired) {
    recommendations.push({ kind: 'rest' });
  }
  if (weakZones.length > 0) {
    recommendations.push({ kind: 'review' });
  }
  if (!tired && input.metrics.accuracy >= TARGET_ACCURACY && weakZones.length === 0) {
    recommendations.push({ kind: 'speed' });
  }
  if (!tired && !input.isControlled && input.metrics.accuracy >= TARGET_ACCURACY) {
    recommendations.push({ kind: 'control-test' });
  }

  return recommendations.slice(0, MAX_RECOMMENDATIONS);
}

/** Собирает полный итог сессии. */
export function buildSessionSummary(input: SummaryInput): SessionOutcomeSummary {
  const weakZones = collectWeakZones(input);

  const xpAwarded: XpTransaction[] = collectXpReasons(input)
    .map((reason) => awardXp(reason, input.xpOccurrencesToday[reason] ?? 0, input.awardedAt))
    .filter((transaction) => transaction.amount > 0);

  const totalXp = input.previousXp + xpAwarded.reduce((sum, item) => sum + item.amount, 0);

  const growth =
    input.baseline === null
      ? null
      : buildGrowthReport(input.baseline, input.metrics, input.isControlled);

  const indicators = computeIndicators({
    baseline: input.baseline ?? {
      language: 'ru',
      layoutId: 'ru-jcuken',
      wpm: input.metrics.wpm,
      cpm: input.metrics.cpm,
      accuracy: input.metrics.accuracy,
      measuredAt: input.awardedAt,
    },
    currentWpm: input.metrics.wpm,
    currentAccuracy: input.metrics.accuracy,
    totalXp,
    activeDays: input.activeDays,
    retained: input.retained,
  });

  return {
    metrics: input.metrics,
    growth,
    indicators,
    weakZones,
    xpAwarded,
    totalXp,
    level: levelForXp(totalXp),
    recommendations: buildRecommendations(input, weakZones),
  };
}
